import React, { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/pagination';
import SwiperCore from 'swiper';
import { FreeMode, Autoplay, Navigation } from 'swiper/modules';

SwiperCore.use([FreeMode, Autoplay, Navigation]);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://medical-linkers.onrender.com';

const NewsCarousel = ({ title = "News & Events", limit = 100 }) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const swiperRef = useRef(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(`${API_BASE_URL}/api/news?status=published&limit=${limit}`);
        
        if (response.ok) {
          const data = await response.json();
          setNews(data.news || []);
        } else {
          throw new Error('Failed to fetch news');
        }
      } catch (error) {
        console.error('Error fetching news:', error);
        setError('Failed to load news');
        // Fallback to default news data
        setNews([
          {
            id: 1,
            image: "/images/image copy 62.png",
            headline: "India's Healthcare Sector Budget Allocation 2025",
            excerpt: "Finance Minister announces significant budget allocation for India's healthcare sector enhancement and PLI scheme.",
            category: "Healthcare Policy",
            publishingDate: "2025-02-01",
            minRead: 2,
            articleLink: "https://www.cnbctv18.com/budget/india-healthcare-sector-budget-2025-allocation-fm-sitharaman-pli-19549128.htm"
          },
          {
            id: 2,
            image: "/images/image copy 63.png",
            headline: "Medical Linkers Founder Supports Budget Proposals",
            excerpt: "Ms. Sonam Sharma urges support for budget proposals to enhance India's medical tourism and healthcare infrastructure.",
            category: "Medical Tourism",
            publishingDate: "2025-01-28",
            minRead: 3,
            articleLink: "https://businessheadline.in/medical-linkers-founder-ms-sonam-sharma-urges-support-for-budget-proposals-to-enhance-indias-medical-tourism-and-healthcare-infrastructure/"
          },
          {
            id: 3,
            image: "/images/image copy 64.png",
            headline: "Best Health And Recovery Medical Tourism Companies",
            excerpt: "Recognition of leading medical tourism companies providing critical surgery aftercare and recovery services.",
            category: "Industry Recognition",
            publishingDate: "2025-01-15",
            minRead: 2,
            articleLink: "https://businessnewsthisweek.com/business/best-health-and-recovery-medical-tourism-companies-for-critical-surgery-aftercare/"
          },
          {
            id: 4,
            image: "/images/image copy 61.png",
            headline: "Medical Tourism Excellence in India",
            excerpt: "India continues to lead in medical tourism excellence with world-class healthcare facilities and affordable treatments.",
            category: "Healthcare Excellence",
            publishingDate: "2025-01-10",
            minRead: 2,
            articleLink: "https://www.medicaltourism.com/india-healthcare-excellence"
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [limit]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleNewsClick = (articleLink) => {
    if (articleLink) {
      window.open(articleLink, '_blank', 'noopener noreferrer');
    }
  };

  const renderStatus = (message) => (
    <section className="container mx-auto px-4 py-16">
      <h2 className="text-4xl font-bold text-center mb-12">
        <span className="text-[#1976f2]">News</span> & Events
      </h2>
      <div className="text-center text-lg">{message}</div>
    </section>
  );

  if (loading) return renderStatus("Loading news...");
  if (error && news.length === 0) return renderStatus(`Error: ${error}`);
  if (!news || news.length === 0) return renderStatus("No news found.");

  return (
    <section className="container mx-auto px-4 py-16">
      <h2 className="text-4xl font-bold text-center mb-12">
        <span className="text-[#1976f2]">News</span> & Events
      </h2>
      
      <div className="overflow-hidden relative">
        <Swiper
          ref={swiperRef}
          slidesPerView={1}
          spaceBetween={16}
          loop={true}
          freeMode={true}
          autoplay={{ delay: 3000, disableOnInteraction: false }}
          speed={1000}
          grabCursor={true}
          navigation={{
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          }}
          breakpoints={{
            640: {
              slidesPerView: 2,
              spaceBetween: 20,
            },
            1024: {
              slidesPerView: 3,
              spaceBetween: 24,
            },
          }}
          className="w-full"
        >
          {[...news, ...news].map((newsItem, index) => (
            <SwiperSlide key={`${newsItem.id || newsItem._id}-${index}`}>
              <div className="transition-transform duration-300 ease-in-out transform">
                <div className="bg-white rounded-lg overflow-hidden shadow-md transition-shadow">
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={newsItem.image || "/images/image copy 61.png"} 
                      alt={newsItem.headline} 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = "/images/image copy 61.png";
                      }}
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                      <span className="bg-[#1976f2] text-white px-2 py-1 rounded text-xs">
                        {newsItem.category}
                      </span>
                      <span>{formatDate(newsItem.publishingDate)}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-3 leading-tight">
                      <button 
                        onClick={() => handleNewsClick(newsItem.articleLink)}
                        className="text-left w-full"
                      >
                        {newsItem.headline}
                      </button>
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {newsItem.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <button 
                        onClick={() => handleNewsClick(newsItem.articleLink)}
                        className="text-[#1976f2] font-medium inline-flex items-center"
                      >
                        Read More <span className="ml-1">→</span>
                      </button>
                      <span className="text-xs text-gray-400">
                        {newsItem.minRead} min read
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Custom Navigation Buttons */}
        <div className="flex justify-center gap-4 mt-10">
          <button
            onClick={() => swiperRef.current?.swiper.slidePrev()}
            aria-label="Previous"
            className="w-12 h-12 rounded-full flex items-center justify-center bg-[#1976f2] text-white"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={() => swiperRef.current?.swiper.slideNext()}
            aria-label="Next"
            className="w-12 h-12 rounded-full flex items-center justify-center bg-[#1976f2] text-white"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
};

export default NewsCarousel;
